import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { authJsonHeaders, clearFakeAuthStorage, fetchCsrfToken } from '../utils/authClient';

const SignInContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const SignInCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 500px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #ffffff;
  font-weight: 500;
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00ff88;
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.3);
  }

  &::placeholder {
    color: #888888;
  }

  &.error {
    border-color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 255, 136, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid #ff4444;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #ff4444;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const SuccessMessage = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00ff88;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00d4ff;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const SignUpLink = styled.div`
  text-align: center;
  margin-top: 2rem;
  color: #cccccc;
  font-size: 0.9rem;
`;

const LinkStyled = styled(Link)`
  color: #00ff88;
  text-decoration: none;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: center;
  margin-top: 1rem;
`;

const ForgotPasswordButton = styled.button`
  background: none;
  border: none;
  color: #00d4ff;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    color: #00ff88;
  }
`;

const SignIn = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Username or email is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      clearFakeAuthStorage();
      const csrfToken = await fetchCsrfToken();
      const response = await fetch('/api/signin', {
        method: 'POST',
        credentials: 'include',
        headers: authJsonHeaders(csrfToken),
        body: JSON.stringify({
          usernameOrEmail: formData.usernameOrEmail,
          password: formData.password
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setErrors({ submit: data.message || 'Unable to sign in.' });
      }
    } catch (error) {
      setErrors({ submit: 'Unable to sign in. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implement forgot password functionality
    alert('Forgot password functionality coming soon!');
  };

  return (
    <SignInContainer>
      <SignInCard>
        <Title>Welcome Back</Title>
        <Subtitle>Sign in to your Skylink account</Subtitle>

        <InfoBox>
          <strong>Don't have an account?</strong> Create one to start connecting with your loved ones through secure communication.
        </InfoBox>

        {success && (
          <SuccessMessage>
            ✅ Successfully signed in! Redirecting to dashboard...
          </SuccessMessage>
        )}

        {errors.submit && (
          <ErrorMessage>❌ {errors.submit}</ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="usernameOrEmail">Username or Email</Label>
            <Input
              type="text"
              id="usernameOrEmail"
              name="usernameOrEmail"
              value={formData.usernameOrEmail}
              onChange={handleChange}
              className={errors.usernameOrEmail ? 'error' : ''}
              placeholder="Enter your username or email"
              required
            />
            {errors.usernameOrEmail && <ErrorMessage>{errors.usernameOrEmail}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
              required
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
          </FormGroup>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing In...' : 'Sign In'}
          </SubmitButton>
        </Form>

        <ForgotPasswordLink>
          <ForgotPasswordButton onClick={handleForgotPassword}>
            Forgot your password?
          </ForgotPasswordButton>
        </ForgotPasswordLink>

        <SignUpLink>
          Don't have an account?{' '}
          <LinkStyled to="/signup">Sign up here</LinkStyled>
        </SignUpLink>
      </SignInCard>
    </SignInContainer>
  );
};

export default SignIn; 